/**
 * effectsHelpers.js
 * Shared constants, helpers and UI atoms for the EffectsPanel sub-components.
 */
import React from 'react';

// ── Color classification (from DB — skills.color column) ──────────────────────
export const BASE_COLORS = ['Orange Skills', 'Green Skills', 'Purple Skills', 'Red Skills', 'Blue Skills'];
export const SPECIAL_COLORS = ['Evolution Skills', 'Gold Skills', 'Inherited Skills', 'Unique Skills', 'Scenario Gold Skill'];
export const DEBUFF_COLORS = ['Red Skills'];

export const COLOR_STYLE = {
    'Orange Skills': { pill: 'bg-orange-900/50 text-orange-300 border-orange-700/50', dot: 'bg-orange-400' },
    'Green Skills': { pill: 'bg-emerald-900/50 text-emerald-300 border-emerald-700/50', dot: 'bg-emerald-400' },
    'Purple Skills': { pill: 'bg-purple-900/50 text-purple-300 border-purple-700/50', dot: 'bg-purple-400' },
    'Red Skills': { pill: 'bg-rose-900/50 text-rose-300 border-rose-700/50', dot: 'bg-rose-400' },
    'Blue Skills': { pill: 'bg-blue-900/50 text-blue-300 border-blue-700/50', dot: 'bg-blue-400' },
    'Evolution Skills': { pill: 'bg-yellow-900/50 text-yellow-300 border-yellow-700/50', dot: 'bg-yellow-400' },
    'Gold Skills': { pill: 'bg-amber-900/50 text-amber-300 border-amber-700/50', dot: 'bg-amber-400' },
    'Inherited Skills': { pill: 'bg-slate-700/50 text-slate-300 border-slate-600/50', dot: 'bg-slate-400' },
    'Unique Skills': { pill: 'bg-violet-900/50 text-violet-300 border-violet-700/50', dot: 'bg-violet-400' },
    'Scenario Gold Skill': { pill: 'bg-amber-900/50 text-amber-200 border-amber-600/50', dot: 'bg-amber-300' },
};
const defaultStyle = { pill: 'bg-slate-700/50 text-slate-400 border-slate-600/50', dot: 'bg-slate-500' };
export const getStyle = (color) => COLOR_STYLE[color] || defaultStyle;

// ── Sticky class helpers ───────────────────────────────────────────────────────
export const stickyLeft = 'sticky left-0 z-10';
export const stickyRight = 'sticky right-0 z-10';

// ── Data helpers ──────────────────────────────────────────────────────────────
export const getEffectsFromScenario = (skill) => {
    if (!skill.skill_effects?.length) return {};
    const scenario = skill.skill_effects.find(e => e.group_id === skill.chosenScenario)
        || skill.skill_effects[0];
    return scenario?.effects || {};
};

export const parseNumeric = (value) => {
    if (value === null || value === '' || value === undefined) return null;
    const n = parseFloat(value);
    return isNaN(n) ? null : n;
};

export const collectEffectNames = (skills) => {
    const names = new Set();
    skills.forEach(s => Object.keys(getEffectsFromScenario(s)).forEach(n => names.add(n)));
    return [...names].sort();
};

export const fmt = (n) => n % 1 === 0 ? n : n.toFixed(2);

// ── Reusable scrollable table container ───────────────────────────────────────
export const ScrollTable = ({ minWidth, children }) => (
    <div className="w-full min-w-0 overflow-x-auto scrollbar scrollbar-thin
        scrollbar-thumb-slate-600 hover:scrollbar-thumb-slate-500 scrollbar-track-slate-800/30">
        <table className="w-full text-left border-collapse" style={{ minWidth }}>
            {children}
        </table>
    </div>
);

// ── Effect cell ───────────────────────────────────────────────────────────────
export const EffectCell = ({ entries }) => {
    if (!entries || entries.length === 0) return <span className="text-slate-700 text-[11px]">—</span>;
    return (
        <div className="flex flex-col items-center gap-0.5">
            {entries.map(([value, magnitude], i) => (
                <div key={i} className="flex items-baseline gap-1 whitespace-nowrap">
                    <span className="font-mono font-bold text-emerald-400 text-[13px]">
                        {value !== '' && value !== null ? value : '—'}
                    </span>
                    {magnitude && magnitude !== '' && (
                        <span className="text-emerald-600 text-[10px]">{magnitude}</span>
                    )}
                </div>
            ))}
        </div>
    );
};

// ── Collapsible section header ────────────────────────────────────────────────
export const SectionHeader = ({ open, onToggle, dotColor, title, subtitle }) => (
    <button onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5
            bg-slate-900/40 hover:bg-slate-900 transition-colors rounded-xl border border-slate-700">
        <div className="flex items-center gap-2">
            <div className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{title}</span>
            {subtitle && <span className="text-[10px] font-mono text-slate-600">{subtitle}</span>}
        </div>
        <svg className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
    </button>
);

// ── Effect name sticky cell ───────────────────────────────────────────────────
export const EffectNameCell = ({ name, rowIdx, isSticky = true }) => (
    <td className={`${isSticky ? stickyLeft : ''} px-4 py-2.5 border-r border-slate-700/40
        ${rowIdx % 2 === 0 ? 'bg-slate-800/90' : 'bg-slate-900/80'}`}>
        <span className="font-mono text-[11px] font-semibold text-slate-300 whitespace-nowrap">{name}</span>
    </td>
);
